import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, History, Loader2, RefreshCw, ShieldCheck, Sparkles } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { Card, CardContent, CardHeader } from '@/components/common/Card'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { AdvisorPanel } from '@/components/patching/AdvisorPanel'
import { CampaignReport } from '@/components/patching/CampaignReport'
import { DevActorSwitch } from '@/components/patching/DevActorSwitch'
import { AuditTrail } from '@/components/patching/AuditTrail'
import { EnvStagePanel } from '@/components/patching/EnvStagePanel'
import { ScopeSummary } from '@/components/patching/ScopeSummary'
import { StageStepper } from '@/components/patching/StageStepper'
import { usePatchCampaign, usePatchMe } from '@/hooks/usePatching'
import { formatDateTime } from '@/utils/format'
import { campaignBucket, campaignHeadline, findStage, hasRunningStage, isStageIncluded, PATCH_POLL_INTERVAL_MS } from '@/utils/patchStage'
import { isValidCampaignId } from '@/utils/patchValidation'
import { PATCH_ENVIRONMENTS, type PatchCampaign, type PatchEnvironment } from '@/types/patching'
import shared from '@/components/patching/Patching.module.css'
import styles from './PatchCampaignDetailPage.module.css'

type TabId = 'overview' | 'audit' | 'advisor' | 'report'

/** The environment that needs attention: the first included stage that is not yet accepted. */
function defaultEnvironment(campaign: PatchCampaign): PatchEnvironment {
  const included = PATCH_ENVIRONMENTS.filter((env) => isStageIncluded(findStage(campaign, env)))
  return included.find((env) => findStage(campaign, env)?.status !== 'accepted') ?? included[included.length - 1] ?? 'dev'
}

function BackLink() {
  return (
    <Link to="/patch-management" className={styles.backLink}>
      <ArrowLeft size={14} /> All campaigns
    </Link>
  )
}

export function PatchCampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>()
  const idValid = isValidCampaignId(campaignId)
  const { data: campaign, isLoading, isError, error, refetch, isFetching } = usePatchCampaign(campaignId)
  const { data: me, isLoading: meLoading, error: meError } = usePatchMe()
  const [params, setParams] = useSearchParams()
  const requestedTab = params.get('tab')
  const tab: TabId = requestedTab === 'audit' || requestedTab === 'advisor' || requestedTab === 'report' ? requestedTab : 'overview'
  const [selectedEnv, setSelectedEnv] = useState<PatchEnvironment | null>(null)

  function selectTab(next: TabId) {
    const nextParams = new URLSearchParams(params)
    if (next === 'overview') nextParams.delete('tab')
    else nextParams.set('tab', next)
    setParams(nextParams, { replace: true })
  }

  if (!idValid) {
    return (
      <div className={styles.page}>
        <BackLink />
        <div className={clsx(shared.alert, shared.alertError)} role="alert" data-testid="invalid-id">
          <AlertTriangle size={16} className={shared.alertIcon} />
          <span>“{campaignId}” is not a valid campaign id (expected PCH-YYYY-NNNN).</span>
        </div>
      </div>
    )
  }
  if (!campaign && isLoading) return <LoadingSpinner label="Loading campaign…" />
  if (!campaign) {
    return (
      <div className={styles.page}>
        <BackLink />
        <div className={clsx(shared.alert, shared.alertError)} role="alert" data-testid="campaign-error">
          <AlertTriangle size={16} className={shared.alertIcon} />
          <span>Could not load campaign {campaignId}: {error?.message ?? 'not found'}</span>
        </div>
        <div>
          <Button variant="secondary" onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    )
  }

  const refreshError = isError ? error : null
  const env = selectedEnv ?? defaultEnvironment(campaign)
  const stage = findStage(campaign, env)
  const live = hasRunningStage(campaign)
  const bucket = campaignBucket(campaign)

  return (
    <div className={styles.page}>
      <BackLink />

      {refreshError && (
        <div className={clsx(shared.alert, shared.alertWarning)} role="alert" data-testid="refresh-error">
          <AlertTriangle size={16} className={shared.alertIcon} />
          <span>Could not refresh this campaign — showing the last data received. {refreshError.message}</span>
        </div>
      )}

      <Card>
        <CardHeader
          title={
            <span className={styles.titleRow}>
              <ShieldCheck size={18} /> {campaign.name}
            </span>
          }
          subtitle={
            <span>
              <span className={shared.mono}>{campaign.id}</span> ·{' '}
              <span data-testid="campaign-headline" data-bucket={bucket}>{campaignHeadline(campaign)}</span>
            </span>
          }
          actions={
            <>
              {live && (
                <span className={clsx(shared.badge, shared.badgeActive)} data-testid="live-indicator" title={`Refreshing every ${PATCH_POLL_INTERVAL_MS / 1000}s while a stage is running`}>
                  <Loader2 size={11} className={shared.spin} /> Live
                </span>
              )}
              <DevActorSwitch />
              <Button variant="secondary" size="sm" icon={<RefreshCw size={13} />} onClick={() => refetch()} disabled={isFetching}>
                Refresh
              </Button>
            </>
          }
        />
        <CardContent className={shared.stack}>
          {campaign.description && <p className={shared.bodyText}>{campaign.description}</p>}
          <dl className={shared.factGrid}>
            <div><dt className={shared.factLabel}>Product</dt><dd className={shared.factValue}>{campaign.product || '—'}</dd></div>
            <div><dt className={shared.factLabel}>Change ticket</dt><dd className={shared.factValue}>{campaign.change_ticket || '—'}</dd></div>
            <div><dt className={shared.factLabel}>Requested by</dt><dd className={shared.factValue}>{campaign.requested_by_name || campaign.requested_by || '—'}</dd></div>
            <div><dt className={shared.factLabel}>Created</dt><dd className={shared.factValue}>{campaign.created_at ? formatDateTime(campaign.created_at) : '—'}</dd></div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Patch scope" subtitle="Identical across environments and bound to every approval." />
        <CardContent>
          <ScopeSummary scope={campaign.patch_scope} scopeHash={campaign.scope_hash} />
        </CardContent>
      </Card>

      <div>
        <StageStepper campaign={campaign} selected={env} onSelect={setSelectedEnv} />
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Campaign sections">
        {([
          ['overview', `${env.toUpperCase()} environment`, null],
          ['audit', 'Audit trail', <History key="a" size={13} />],
          ['advisor', 'AI advisor', <Sparkles key="s" size={13} />],
          ['report', 'Report', null],
        ] as const).map(([id, label, icon]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            className={clsx(styles.tab, tab === id && styles.tabActive)}
            onClick={() => selectTab(id)}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' &&
        (stage ? (
          <EnvStagePanel
            key={stage.environment}
            campaign={campaign}
            stage={stage}
            me={me}
            meLoading={meLoading}
            meError={meError ? meError.message : null}
          />
        ) : (
          <p className={shared.muted} data-testid="no-stage">
            {env.toUpperCase()} is not part of this campaign.
          </p>
        ))}

      {tab === 'audit' && (
        <Card>
          <CardHeader title="Audit trail" subtitle="Append-only record of every action on this campaign." />
          <CardContent>
            <AuditTrail campaignId={campaign.id} active />
          </CardContent>
        </Card>
      )}

      {tab === 'report' && (
        <Card>
          <CardHeader title="Campaign report" subtitle="Execution results, schedules and pre-patch checks. No credentials." />
          <CardContent>
            <CampaignReport campaignId={campaign.id} />
          </CardContent>
        </Card>
      )}

      {tab === 'advisor' && (
        <Card>
          <CardHeader title="AI advisor" subtitle="Read-only summary of the campaign state." />
          <CardContent>
            <AdvisorPanel campaignId={campaign.id} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}
